import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import moment from "moment";
import type { View } from "react-big-calendar";
import { DEFAULT_PLANNER_FILTERS, normalizePlannerFilters, type PlannerFilters } from "@common/services/planner-filters";


export interface CalendarDashboardState {
    contactPicMap?: Record<string, string>, /// [waId]:profilePicURL
    selectedEventDate?: string,  // Store the original date of selected event
    calendarMode: 'calendar' | 'agenda' | null;
    currentDate: Date;
    view: View;
    /** The planner's filters. The ONE interpretation of them lives in
     *  @common/services/planner-filters (kinds × outcomes, plus the recipient axis) —
     *  do not re-derive the rule anywhere else. Not persisted: redux only. */
    calendarSidebarFilters: PlannerFilters;
    /** "Show this on the calendar", asked from a sidebar row: the grid moves to
     *  the occurrence, scrolls it into view and flashes it. `nonce` makes a
     *  repeat request for the same item a new request. */
    revealRequest: CalendarRevealRequest | null;
}

export interface CalendarRevealRequest {
    /** The document id the chip carries (a campaign's or a message's). */
    id: string;
    isCampaign: boolean;
    /** This occurrence's due date — recurring messages share one id. */
    dueDate: string;
    nonce: number;
}

const initialState: CalendarDashboardState = {
    contactPicMap: {},
    selectedEventDate: undefined,
    calendarMode: 'calendar',
    currentDate: moment().toDate(),
    view: 'month',
    calendarSidebarFilters: { ...DEFAULT_PLANNER_FILTERS },
    revealRequest: null,
};

const CalendarSlice = createSlice({
    name: "Calendars",
    initialState,
    reducers: {
        setContactPicMap: (state, { payload }: PayloadAction<[string, string]>) => {
            state.contactPicMap = { ...state.contactPicMap, [payload[0]]: payload[1] }
        },
        setSelectedEventDate: (state, { payload }: PayloadAction<string>) => {
            state.selectedEventDate = payload;
        },
        setCalendarMode: (state, { payload }: PayloadAction<'calendar' | 'agenda' | null>) => {
            state.calendarMode = payload;
        },
        setCurrentDate: (state, { payload }: PayloadAction<Date>) => {
            state.currentDate = payload;
        },
        setView: (state, { payload }: PayloadAction<View>) => {
            state.view = payload;
        },
        // The chips only exist in calendar mode, so a reveal leaves the agenda.
        revealCalendarOccurrence: {
            reducer: (state, { payload }: PayloadAction<CalendarRevealRequest>) => {
                state.revealRequest = payload;
                state.calendarMode = 'calendar';
            },
            prepare: (request: Omit<CalendarRevealRequest, 'nonce'>) => ({ payload: { ...request, nonce: Date.now() } }),
        },
        // Normalised on the way IN, so the store can only ever hold the current shape —
        // a caller still passing the old one (`showPast`, the status chips) is migrated
        // here rather than leaving a missing key to read as "off".
        setCalendarSidebarFilters: (state, { payload }: PayloadAction<Partial<PlannerFilters> | Record<string, unknown>>) => {
            state.calendarSidebarFilters = normalizePlannerFilters(payload);
        }
    },
});

export const {
    setContactPicMap,
    setSelectedEventDate,
    setCalendarMode,
    setCurrentDate,
    setView,
    revealCalendarOccurrence,
    setCalendarSidebarFilters
} = CalendarSlice.actions;
export default CalendarSlice.reducer;


